import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { READ_INCIDENTS_PERMISSION, can, useIdentity } from '@/entities/session';
import { useIncidentQueue } from '@/features/incidents';
import { scoreBand, useDatasetTrend } from '@/features/scoring';
import { ApiError } from '@/shared/api/http';
import type { Score } from '@/shared/api/generated/scoring';
import { ageText } from '@/shared/model/age';
import { compactNumber } from '@/shared/model/number';
import { SCREEN_PATHS } from '@/shared/routes';
import { HelpLabel, InfoTip } from '@/shared/ui/InfoTip';

import type { ProfileReading } from '../model/catalogue';

/* The design's four figure tiles across the top of a dataset: its rule score, what profiling alone
 * says, how many rows the last profile scanned, and how many incidents are open against it.
 *
 * **Every tile has a sentence for each way its figure can be absent**, because each absence means
 * something different and a zero would mean something false. A dataset no execution has covered is
 * "never scored", which is the ordinary state and not a failure; a score whose dimensions held no
 * evidence is "not computable", which is a finding; and a read that failed says so rather than
 * either. The baseline and the row count share one reading, and "never profiled" there is not "no
 * rows" — the rule the catalogue table's `ProfileCell` holds for the same figure.
 *
 * **The baseline is marked provisional beside the rule score, never blended with it**, which is the
 * contract's own instruction for `profile_baseline`: a screen showing both must say which is which.
 */

const NOT_FOUND = 404;
/** The window the delta is read over — the scoring screens' own default. */
const TREND_DAYS = 30;

/** The profile copy for each way a row count can be absent. */
const ABSENT_PROFILE = {
  neverProfiled: 'never',
  profiledEmpty: 'empty',
  unread: 'unread',
} as const satisfies Record<Exclude<ProfileReading['kind'], 'measured'>, string>;

const BAND_LABEL = {
  ok: 'dataset.tile.band.ok',
  warn: 'dataset.tile.band.warn',
  danger: 'dataset.tile.band.danger',
} as const satisfies Record<ReturnType<typeof scoreBand>, string>;

export interface DatasetFiguresProps {
  readonly datasetId: string;
  readonly score: UseQueryResult<Score>;
  readonly reading: ProfileReading;
  /** The profile facts' baseline, when the facts were read. */
  readonly baseline: number | null | undefined;
}

export function DatasetFigures({
  datasetId,
  score,
  reading,
  baseline,
}: DatasetFiguresProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="lq-dd__tiles" role="group" aria-label={t('dataset.tile.region')}>
      <ScoreTile datasetId={datasetId} score={score} />
      <BaselineTile reading={reading} baseline={baseline} />
      <RowsTile reading={reading} />
      <IncidentsTile datasetId={datasetId} />
    </div>
  );
}

/** The score tile's label, the same in every state the tile can be in. */
function ScoreLabel(): ReactElement {
  const { t } = useTranslation();
  return (
    <HelpLabel subject={t('dataset.tile.score')} help={t('help.feature.qualityScore')}>
      <div className="lq-dd__tileLabel">{t('dataset.tile.score')}</div>
    </HelpLabel>
  );
}

function ScoreTile({
  datasetId,
  score,
}: {
  readonly datasetId: string;
  readonly score: UseQueryResult<Score>;
}): ReactElement {
  const { t } = useTranslation();
  /* Only asked for once there is a score to compare against: a dataset no execution has covered has
     no trend either, and the request would be a guaranteed 404 on every visit. */
  const trend = useDatasetTrend(score.isSuccess ? datasetId : '', TREND_DAYS);

  if (score.isPending) {
    return (
      <div className="lq-dd__tile">
        <ScoreLabel />
        <div className="lq-dd__tileValue lq-dd__tileValue--quiet" role="status">
          {t('dataset.score.loading')}
        </div>
      </div>
    );
  }

  if (score.isError) {
    const notScored = score.error instanceof ApiError && score.error.status === NOT_FOUND;
    return (
      <div className="lq-dd__tile">
        <ScoreLabel />
        <div className="lq-dd__tileValue lq-dd__tileValue--quiet">
          {notScored ? t('dataset.tile.neverScored') : t('dataset.tile.scoreUnread')}
        </div>
        {/* The design's empty coverage bar: nothing has a rule that produced evidence. Only for a
            dataset never scored — a failed read has no coverage to draw, empty or otherwise. */}
        {notScored ? <div className="lq-dd__coverage" aria-hidden="true" /> : null}
        <div className="lq-dd__tileSub">
          {notScored ? t('dataset.tile.noEvidence') : score.error.message}
        </div>
      </div>
    );
  }

  const { value, coverage } = score.data;
  const band = value === null ? null : scoreBand(value);
  const points = (trend.data?.points ?? []).filter(
    (point): point is typeof point & { readonly value: number } => point.value !== null,
  );
  const first = points[0];
  const last = points.at(-1);
  const delta =
    first !== undefined && last !== undefined && points.length >= 2
      ? last.value - first.value
      : null;

  return (
    <div className="lq-dd__tile">
      <ScoreLabel />
      <div className="lq-dd__tileHead">
        <span
          className={
            value === null
              ? 'lq-dd__tileValue lq-dd__tileValue--quiet'
              : 'lq-dd__tileValue lumiq-numeric'
          }
        >
          {value === null ? t('dataset.tile.notComputable') : value.toFixed(1)}
        </span>
        <span className="lq-dd__band" data-band={band ?? 'unknown'}>
          {band === null ? t('dataset.tile.notComputable') : t(BAND_LABEL[band])}
        </span>
      </div>
      {delta !== null && Math.abs(delta) >= 0.05 ? (
        <div className="lq-dd__trend" data-direction={delta < 0 ? 'down' : 'up'}>
          {t(delta < 0 ? 'dataset.tile.trendDown' : 'dataset.tile.trendUp', {
            delta: Math.abs(delta).toFixed(1),
            count: points.length,
          })}
        </div>
      ) : null}
      {/* Coverage under the figure, because it is what makes the figure honest: a 96 over two
          columns of forty is a different object from a 96 over all forty. */}
      <div className="lq-dd__coverage" aria-hidden="true">
        <div
          className="lq-dd__coverageFill"
          style={{ width: `${String(Math.round((coverage.column_ratio ?? 0) * 100))}%` }}
        />
      </div>
      <div className="lq-dd__tileSub">
        {t('dataset.tile.coverage', {
          covered: coverage.columns_with_a_rule,
          total: coverage.columns_total,
        })}
      </div>
    </div>
  );
}

function BaselineTile({
  reading,
  baseline,
}: {
  readonly reading: ProfileReading;
  readonly baseline: number | null | undefined;
}): ReactElement {
  const { t } = useTranslation();
  /* Null is two different facts and `reading` is what separates them — the contract's own words:
     never profiled, or profiled with every column unmeasured. Neither is a zero. */
  const text =
    reading.kind === 'unread'
      ? t('catalogue.profile.unread.label')
      : baseline === null || baseline === undefined
        ? reading.kind === 'neverProfiled'
          ? t('catalogue.profile.never.label')
          : '—'
        : null;

  return (
    <div className="lq-dd__tile">
      <div className="lq-dd__tileLabel">
        {t('dataset.tile.baseline')}
        {/* Beside the term, before the pill: the explanation is of "Profile baseline", and an "i"
            after "Provisional" would read as explaining that word instead. */}
        <InfoTip
          subject={t('dataset.tile.baseline')}
          text={t('help.metric.profileBaseline')}
        />
        <span className="lq-dd__provisional">{t('dataset.tile.provisional')}</span>
      </div>
      <div
        className={
          text === null
            ? 'lq-dd__tileValue lumiq-numeric'
            : 'lq-dd__tileValue lq-dd__tileValue--quiet'
        }
      >
        {text ?? String(baseline)}
      </div>
      <div className="lq-dd__tileSub">{t('dataset.tile.baselineNote')}</div>
    </div>
  );
}

function RowsTile({ reading }: { readonly reading: ProfileReading }): ReactElement {
  const { t } = useTranslation();
  const at =
    reading.kind === 'measured' || reading.kind === 'profiledEmpty' ? reading.at : null;
  return (
    <div className="lq-dd__tile">
      <div className="lq-dd__tileLabel">{t('dataset.tile.rows')}</div>
      <div
        className={
          reading.kind === 'measured'
            ? 'lq-dd__tileValue lumiq-numeric'
            : 'lq-dd__tileValue lq-dd__tileValue--quiet'
        }
        title={
          reading.kind === 'measured' ? t('catalogue.profile.rowsMeaning') : undefined
        }
      >
        {reading.kind === 'measured'
          ? compactNumber(reading.rows)
          : t(`catalogue.profile.${ABSENT_PROFILE[reading.kind]}.label`)}
      </div>
      <div className="lq-dd__tileSub">
        {at === null
          ? t('dataset.tile.rowsNever')
          : t('dataset.tile.rowsWhen', { at: ageText(t, at, new Date()) ?? at })}
      </div>
    </div>
  );
}

function IncidentsTile({ datasetId }: { readonly datasetId: string }): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const allowed = can(identity.data, READ_INCIDENTS_PERMISSION);
  const queue = useIncidentQueue({ enabled: allowed });
  /* Read off the typed `subject_kind` rather than inferred from `kind`: a contract breach's subject
     is the contract, so matching its id against a dataset's counts the wrong thing. */
  const open = (queue.data ?? []).filter(
    (incident) =>
      incident.active &&
      incident.subject_kind === 'dataset' &&
      incident.subject_id === datasetId,
  ).length;

  return (
    <Link className="lq-dd__tile lq-dd__tile--link" to={SCREEN_PATHS.incidents}>
      <span className="lq-dd__tileLabel">{t('dataset.tile.incidents')}</span>
      <span
        className={
          queue.isSuccess
            ? 'lq-dd__tileValue lumiq-numeric'
            : 'lq-dd__tileValue lq-dd__tileValue--quiet'
        }
        data-alert={queue.isSuccess && open > 0 ? 'true' : undefined}
      >
        {!allowed
          ? t('dataset.tile.incidentsHidden')
          : queue.isSuccess
            ? String(open)
            : queue.isError
              ? t('dataset.tile.incidentsUnread')
              : '…'}
      </span>
      <span className="lq-dd__tileLink">{t('dataset.tile.openQueue')}</span>
    </Link>
  );
}
